import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { authRateLimiter } from '@/lib/rateLimiter';
import { validateEmailSecurity, validatePasswordSecurity } from '@/lib/security';
import { errorHandler } from '@/lib/errorHandler';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signInWithFacebook: () => Promise<{ error: string | null }>;
  signInWithTwitter: () => Promise<{ error: string | null }>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation('common');

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Set/clear Sentry user context
        if (session?.user) {
          setSentryUser({ id: session.user.id, email: session.user.email });
        } else {
          clearSentryUser();
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      // Input validation
      const emailValidation = validateEmailSecurity(email);
      if (!emailValidation.isValid) {
        return { error: emailValidation.errors[0] };
      }
      
      // Rate limiting
      const rateLimitCheck = authRateLimiter.check(email.toLowerCase());
      if (!rateLimitCheck.allowed) {
        const retryAfterMinutes = Math.ceil((rateLimitCheck.retryAfter || 0) / 60000);
        return { 
          error: `Too many login attempts. Please try again in ${retryAfterMinutes} minutes.` 
        };
      }
      
      const { error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (error) {
        // Log failed attempt for rate limiting
        errorHandler.handle(error, { context: 'signin', email });
        return { error: 'Invalid email or password' }; // Generic error message
      }

      // Reset rate limit on successful login
      authRateLimiter.reset(email.toLowerCase());

      toast({
        title: t('auth.toast.welcomeBackTitle'),
        description: t('auth.toast.welcomeBackDescription'),
      });

      return { error: null };
    } catch (error: any) {
      errorHandler.handle(error, { context: 'signin' });
      return { error: 'An unexpected error occurred. Please try again.' };
    }
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      // Input validation
      const emailValidation = validateEmailSecurity(email);
      if (!emailValidation.isValid) {
        return { error: emailValidation.errors[0] };
      }
      
      const passwordValidation = validatePasswordSecurity(password);
      if (!passwordValidation.isValid) {
        return { error: passwordValidation.errors[0] };
      }
      
      // Rate limiting
      const rateLimitCheck = authRateLimiter.check(email.toLowerCase());
      if (!rateLimitCheck.allowed) {
        const retryAfterMinutes = Math.ceil((rateLimitCheck.retryAfter || 0) / 60000);
        return { 
          error: `Too many registration attempts. Please try again in ${retryAfterMinutes} minutes.` 
        };
      }

      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName?.trim(),
            display_name: fullName?.trim(),
          }
        }
      });

      if (error) {
        errorHandler.handle(error, { context: 'signup', email });
        
        // Provide user-friendly error messages
        if (error.message.includes('User already registered')) {
          return { error: 'An account with this email already exists' };
        }
        
        if (error.message.includes('Password')) {
          return { error: 'Password does not meet security requirements' };
        }
        
        return { error: 'Registration failed. Please try again.' };
      }

      toast({
        title: t('auth.toast.accountCreatedTitle'),
        description: t('auth.toast.accountCreatedDescription'),
      });

      return { error: null };
    } catch (error: any) {
      errorHandler.handle(error, { context: 'signup' });
      return { error: 'An unexpected error occurred. Please try again.' };
    }
  };

  // Generic stable string returned for OAuth failures so the UI layer can i18n it.
  // Avoids leaking raw English provider error text (mem://i18n/localization-architecture).
  const OAUTH_FAILED = 'OAuth sign-in failed. Please try again.';

  const signInWithOAuthProvider = async (
    provider: 'google' | 'facebook' | 'twitter',
    context: string,
  ) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/` },
      });
      if (error) {
        errorHandler.handle(error, { context });
        return { error: OAUTH_FAILED };
      }
      return { error: null };
    } catch (error: any) {
      errorHandler.handle(error, { context });
      return { error: OAUTH_FAILED };
    }
  };

  const signInWithGoogle = () => signInWithOAuthProvider('google', 'oauth_google');
  const signInWithFacebook = () => signInWithOAuthProvider('facebook', 'oauth_facebook');
  const signInWithTwitter = () => signInWithOAuthProvider('twitter', 'oauth_twitter');

  const signOut = async () => {
    try {
      // Clear any cached data
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }
      
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      
      if (error) {
        errorHandler.handle(error, { context: 'signout' });
      }
      
      // Clear local storage auth keys
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
      
      toast({
        title: t('auth.toast.signedOutTitle'),
        description: t('auth.toast.signedOutDescription'),
      });

      // onAuthStateChange will clear context state automatically.
      // Soft-navigate so React state, scroll, and unsaved drafts are preserved
      // for any visible UI; route guards will handle redirects from protected pages.
      if (window.location.pathname !== '/') {
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch (error: any) {
      errorHandler.handle(error, { context: 'signout' });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      // Input validation
      const emailValidation = validateEmailSecurity(email);
      if (!emailValidation.isValid) {
        return { error: emailValidation.errors[0] };
      }
      
      // Rate limiting
      const rateLimitCheck = authRateLimiter.check(email.toLowerCase());
      if (!rateLimitCheck.allowed) {
        const retryAfterMinutes = Math.ceil((rateLimitCheck.retryAfter || 0) / 60000);
        return { 
          error: `Too many reset attempts. Please try again in ${retryAfterMinutes} minutes.` 
        };
      }

      const redirectUrl = `${window.location.origin}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
        redirectTo: redirectUrl,
      });

      if (error) {
        errorHandler.handle(error, { context: 'reset_password', email });
        if (error.status === 429 || error.message?.includes('security purposes')) {
          return { error: 'Too many reset requests. Please wait a few minutes before trying again.' };
        }
        return { error: 'Failed to send reset email. Please try again.' };
      }

      toast({
        title: t('auth.toast.resetEmailSentTitle'),
        description: t('auth.toast.resetEmailSentDescription'),
      });

      return { error: null };
    } catch (error: any) {
      errorHandler.handle(error, { context: 'reset_password' });
      return { error: 'An unexpected error occurred. Please try again.' };
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithFacebook,
    signInWithTwitter,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};